import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { cx } from "@/utils/cx";
import { Icons } from '@/assets';

interface DropDownFieldProps {
    name: string;
    label: string;
    options: { label: string; value: string }[];
    value?: string;
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void; // <--- Takes string
    error?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
}

export const DropDownField = forwardRef<HTMLInputElement, DropDownFieldProps>(({
    name,
    label,
    options,
    value,
    onChange,
    onBlur,
    error,
    placeholder = "Select",
    required,
    disabled
}, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });

    const selectedOption = options.find(opt => String(opt.value) === String(value));
    const selectedLabel = selectedOption?.label || "";

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(normalizedSearchTerm));
    const selectedOptionMatchesSearch = selectedOption
        ? !normalizedSearchTerm || selectedOption.label.toLowerCase().includes(normalizedSearchTerm)
        : false;
    const displayOptions = selectedOptionMatchesSearch
        ? [
            selectedOption,
            ...filteredOptions.filter(opt => String(opt.value) !== String(selectedOption?.value)),
        ]
        : filteredOptions;

    const updateDropdownPosition = () => {
        if (!fieldRef.current) return;

        const rect = fieldRef.current.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
        });
    };

    const openDropdown = () => {
        if (disabled) return;

        setSearchTerm("");
        setHighlightedIndex(0);
        updateDropdownPosition();
        setIsOpen(true);
    };

    const closeDropdown = (shouldBlur = true) => {
        if (!isOpen) return;

        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(0);
        if (shouldBlur) onBlur?.(value || "");
    };

    const focusSiblingField = (direction: "next" | "previous") => {
        if (typeof document === "undefined" || !wrapperRef.current) return;

        const focusable = Array.from(
            document.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((element) => {
            const isVisible = element.offsetWidth > 0 || element.offsetHeight > 0 || element.getClientRects().length > 0;
            return isVisible && !dropdownRef.current?.contains(element);
        });

        const currentIndex = focusable.findIndex((element) => wrapperRef.current?.contains(element));
        const targetIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
        focusable[targetIndex]?.focus();
    };

    const handleSelect = (val: string, labelText: string) => {
        onChange?.(val);
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(0);
    };

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as Node;
            const clickedField = wrapperRef.current?.contains(target);
            const clickedDropdown = dropdownRef.current?.contains(target);

            if (!clickedField && !clickedDropdown) {
                closeDropdown();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onBlur, value]);

    useEffect(() => {
        if (!isOpen) return;

        updateDropdownPosition();
        searchInputRef.current?.focus();

        const handleWindowChange = () => updateDropdownPosition();
        window.addEventListener("resize", handleWindowChange);
        window.addEventListener("scroll", handleWindowChange, true);

        return () => {
            window.removeEventListener("resize", handleWindowChange);
            window.removeEventListener("scroll", handleWindowChange, true);
        };
    }, [isOpen]);

    useEffect(() => {
        setHighlightedIndex(0);
    }, [searchTerm]);

    useEffect(() => {
        if (!isOpen) return;

        optionRefs.current[highlightedIndex]?.scrollIntoView({
            block: "nearest",
        });
    }, [highlightedIndex, isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        if (e.key === "Tab") {
            e.preventDefault();
            closeDropdown();
            focusSiblingField(e.shiftKey ? "previous" : "next");
            return;
        }

        if (e.key === "Escape") {
            e.preventDefault();
            closeDropdown(false);
            wrapperRef.current?.querySelector<HTMLInputElement>("input")?.focus();
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!isOpen) {
                openDropdown();
                return;
            }
            setHighlightedIndex((current) =>
                displayOptions.length ? (current + 1) % displayOptions.length : 0
            );
            return;
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (!isOpen) {
                openDropdown();
                return;
            }
            setHighlightedIndex((current) =>
                displayOptions.length ? (current - 1 + displayOptions.length) % displayOptions.length : 0
            );
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            if (!isOpen) {
                openDropdown();
                return;
            }

            const highlightedOption = displayOptions[highlightedIndex];
            if (highlightedOption) {
                handleSelect(highlightedOption.value, highlightedOption.label);
            }
        }
    };

    const dropdown = isOpen && !disabled && typeof document !== "undefined"
        ? createPortal(
            <div
                ref={dropdownRef}
                style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                }}
                className="fixed z-[500] rounded-[10px] border border-gray-200 bg-white shadow-lg"
            >
                <div className="p-2">
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search options here..."
                        className="h-10 w-full text-black text-sm placeholder:text-gray-60 outline-none focus:border-brand"
                    />
                </div>
                <div className="max-h-[200px] overflow-y-auto py-1 scrollbar-hide">
                    {displayOptions.length > 0 ? displayOptions
                    .filter((opt): opt is { label: string; value: string } => Boolean(opt))
                    .map((opt, index) => (
                        <div
                            key={opt.value}
                            ref={(node) => {
                                optionRefs.current[index] = node;
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(opt.value, opt.label);
                            }}
                            className={cx(
                                "px-4 py-3 cursor-pointer text-sm text-gray-700",
                                index === highlightedIndex ? "bg-gray-50" : "hover:bg-gray-50"
                            )}
                        >
                            {opt.label}
                        </div>
                    )) : <div className="px-4 py-3 text-sm text-gray-400">No options found</div>}
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <div ref={wrapperRef} className="flex flex-col gap-2.5 w-full relative">
            <label className="text-base text-gray-90">
                {label} {required && <span className="text-error ml-1">*</span>}
            </label>
            <div
                ref={fieldRef}
                onClick={openDropdown}
                className={cx("relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 cursor-pointer",
                    error ? "bg-[#ff424f]" : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "opacity-60 cursor-not-allowed bg-gray-200"
                )}
            >
                <div className="relative flex items-center bg-white rounded-[8.5px] overflow-hidden">
                    <input
                        ref={ref}
                        type="text"
                        placeholder={placeholder}
                        value={selectedLabel}
                        disabled={disabled}
                        readOnly
                        onFocus={openDropdown}
                        onKeyDown={handleKeyDown}
                        className="w-full h-[51px] px-4 pr-10 bg-transparent text-sm outline-none cursor-pointer"
                    />
                    <div className="absolute right-4"><Icons.ChevronDownIcon className={cx("w-4 h-4 text-gray-500 transition-transform", isOpen && "rotate-180")} /></div>
                </div>
            </div>
            {dropdown}
            {error && <p className="text-xs text-error-60 font-medium">{error}</p>}
        </div>
    );
});

DropDownField.displayName = "DropDownField";
